import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Link, useLocation } from 'react-router-dom';
import Select from 'react-select'
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faTrash,
  faClose,
  faChartBar,
  faPen 
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BE_API_URL, categoryOptions, incomeCategoryOptions } from '../constants';
import { notifyMessage } from '../common';

const MoneyLoverEdit = () => {
  const location = useLocation();
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [amount, setAmount] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [note, setNote] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showInputAmount, setShowInputAmount] = useState(true);
  const [loading, setLoading] = useState(true);
  const [userOptions, setUserOptions] = useState([]);

  const appendCharacter = (char) => {
    setDisplayValue((prevValue) => {
      if (/[0-9.]/.test(char)) {
        const newValue = prevValue + char;
        return newValue;
      }
      return prevValue + char;
    });
  };

  const clearDisplay = () => {
    setDisplayValue('');
    setAmount('');
  };

  const calculateResult = () => {
    try {
      const result = eval(displayValue);
      const formattedResult = numberToCurrency(result);
      setDisplayValue(formattedResult);
      setAmount(formattedResult);
    } catch (error) {
      setDisplayValue('');
      setAmount('');
      notifyMessage('error', 'Invalid calculation');
    }
  };

  const handleSelectChange = (option) => {
    setSelectedOption(option);
  };

  const handleSelectUserChange = (option) => {
    setSelectedUserOption(option);
  };

  const handleSubmitTransaction = () => {
    const id = new URLSearchParams(location.search).get('id');
    const date = `${selectedDate.getFullYear()}-${selectedDate.getMonth() + 1}-${selectedDate.getDate()}`;
    const formatedAmount = amount.replace(/,/g, '');
    const transactionType = incomeCategoryOptions.includes(selectedOption.value) ? 1 : 2;

    fetch(`${BE_API_URL}/api/money-lover/update-transaction/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        amount: formatedAmount, 
        note, 
        category: selectedOption.value, 
        date, 
        user_id: selectedUserOption.value, 
        transaction_type: transactionType 
      }),
    })
      .then(response => response.json())
      .then(data => {
        notifyMessage('success', 'Transaction updated successfully!');
        window.history.back();
      })
      .catch(error => {
        console.error('Error updating transaction:', error);
        notifyMessage('error', 'Failed to update transaction');
      });
  };

  const numberToCurrency = (amount) => {
    return amount.toLocaleString('en-US');
  };

  const handleDeleteTransaction = () => {
    const id = new URLSearchParams(window.location.search).get('id')
    fetch(`${BE_API_URL}/api/money-lover/delete-transaction/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => response.json())
      .then(data => {
        notifyMessage('success', 'Delete transaction success!')
        window.location.href = '/money-lover'
      })
      .catch(error => {
        console.error(error);
        notifyMessage('error', 'Delete transaction failed!')
      });
  }

  useEffect(() => {
    const fetchTransaction = () => {
      const id = new URLSearchParams(location.search).get('id');
      fetch(`${BE_API_URL}/api/money-lover/get-transaction-detail?id=${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then(response => response.json())
        .then(data => {
          setAmount(data.amount);
          setDisplayValue(data.amount);
          setSelectedOption({ value: data.category, label: data.category });
          setSelectedUserOption({ value: data.user_id, label: data.user_name });
          setSelectedDate(new Date(data.date));
          setNote(data.note);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching transaction:', error);
          notifyMessage('error', 'Failed to load transaction');
        });
    };

    fetchTransaction();
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gray-50">
      {loading && (
        <div className="fixed inset-0 bg-gray-50 bg-opacity-75 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      <div className="w-full max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Edit Transaction</h1>
          <button
            onClick={() => window.history.back()}
            className="text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-6">
            {/* Calculator Interface */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Amount</h3>
              </div>

              {showInputAmount ? (
                <div className="space-y-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <span className="text-gray-500">$</span>
                    </div>
                    <input
                      type="text"
                      value={displayValue}
                      readOnly
                      className="w-full pl-10 pr-4 py-3 text-right text-2xl font-bold bg-gray-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    {/* Operation Buttons */}
                    <button
                      onClick={() => appendCharacter('-')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg text-red-600"
                    >
                      -
                    </button>
                    <button
                      onClick={() => appendCharacter('+')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg text-green-600"
                    >
                      +
                    </button>
                    <button
                      onClick={() => appendCharacter('.')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg"
                    >
                      .
                    </button>
                    <button
                      onClick={clearDisplay}
                      className="bg-red-100 hover:bg-red-200 p-4 rounded-lg text-red-600"
                    >
                      C
                    </button>

                    {/* Number Buttons */}
                    {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((num) => (
                      <button
                        key={num}
                        onClick={() => appendCharacter(num.toString())}
                        className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg"
                      >
                        {num}
                      </button>
                    ))}
                    <button
                      onClick={() => appendCharacter('0')}
                      className="bg-gray-100 hover:bg-gray-200 p-4 rounded-lg"
                    >
                      0
                    </button>
                    <button
                      onClick={calculateResult}
                      className="col-span-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                    >
                      =
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setAmount(value);
                      setDisplayValue(value);
                    }}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                  />
                </div>
              )}
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <Select placeholder="Choose Category" value={selectedOption} options={categoryOptions} onChange={handleSelectChange} />
            </div>

            {/* Date Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                dateFormat="dd/MM/yyyy"
                placeholderText="Select date"
                maxDate={new Date()}
                minDate={new Date('2020-01-01')}
              />
            </div>

            {/* Note Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <textarea
                className="w-full mt-2 bg-gray-50 p-4 h-30 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your note here."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={255}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                onClick={handleDeleteTransaction}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                Delete
              </button>
              &nbsp;
              <button
                onClick={handleSubmitTransaction}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center justify-center"
                disabled={!amount || !selectedOption || !selectedDate}
              >
                <FontAwesomeIcon icon={faPen} className="mr-2" />
                Update
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default MoneyLoverEdit;
